import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { DbKeyService } from '../core/security/db-key.service';
import { LoggerService } from './logger.service';
import { SecurityStorage } from './storages/security.storage';
import {
    MainToWorkerMessage,
    WorkerToMainMessage
} from '../core/workers/worker-messages';

/**
 * Owns the background loading of older bookings and inactive customers after the
 * first full sync (Issue #5).
 *
 * All network requests originate here via `HttpClient`, so they pass through
 * `AuthHttpInterceptor` (token injection from `SecurityStorage`) and
 * `ErrorHandlerHttpInterceptor` (401/403 -> central logout, offline queue,
 * telemetry chokepoint). The workers only receive the raw response body and the
 * in-memory encryption key — never the token.
 *
 * DI note: this service deliberately depends only on `HttpClient`,
 * `ToastrService`, `SecurityStorage` and `DbKeyService` — never on `AuthService`
 * or `SyncService` — to avoid dependency cycles.
 */
@Injectable({ providedIn: 'root' })
export class BackgroundDataLoadService {
    private httpClient = inject(HttpClient);
    private toastrService = inject(ToastrService);
    private securityStorage = inject(SecurityStorage);
    private dbKeyService = inject(DbKeyService);
    private logger = inject(LoggerService);

    private bookingsWorker?: Worker;
    private customersWorker?: Worker;

    private stop$ = new Subject<void>();
    private isStopped = false;

    /**
     * Starts the background loading. Bookings are always loaded; inactive
     * customers only for admin users.
     */
    start(): void {
        this.isStopped = false;

        this.loadBookings();

        if (this.securityStorage.getUserInfo()?.isAdmin) {
            this.loadCustomers();
        }
    }

    /**
     * Cancels in-flight requests and terminates the workers. Called on logout to
     * ensure no token-bearing request or IndexedDB write can complete after the
     * session ends.
     */
    stop(): void {
        this.isStopped = true;
        this.stop$.next();

        this.terminateWorker('bookingsWorker');
        this.terminateWorker('customersWorker');
    }

    private loadBookings(): void {
        this.toastrService.info(
            'Ältere Buchungen werden im Hintergrund nachgeladen',
            'Hintergrundprozess gestartet'
        );

        this.httpClient
            .get(
                environment.baseUrl +
                    '/api/v2/bookings/get-updated?isFirstLoad=true&onlyTwoYears=false',
                { responseType: 'text' }
            )
            .pipe(takeUntil(this.stop$))
            .subscribe({
                next: rawJson => this.persistBookings(rawJson),
                error: () =>
                    this.toastrService.error(
                        'Beim bearbeiten der älteren Buchungen trat ein unerwarteter Fehler auf',
                        'Fehler'
                    )
            });
    }

    private loadCustomers(): void {
        this.toastrService.info(
            'Synchronisieren inaktiver Kunden',
            'Hintergrundprozess gestartet'
        );

        this.httpClient
            .get(
                environment.baseUrl +
                    '/api/v2/customers/get-updated?isFirstLoad=true&onlyActive=false',
                { responseType: 'text' }
            )
            .pipe(takeUntil(this.stop$))
            .subscribe({
                next: rawJson => this.persistCustomers(rawJson),
                error: () =>
                    this.toastrService.error(
                        'Beim Synchronisieren der inaktiven Kunden ist ein Fehler aufgetreten',
                        'Fehler'
                    )
            });
    }

    private persistBookings(rawJson: string): void {
        // Guard against a logout that happened between request start and response
        // arrival: no worker is spun up and no data is written.
        if (this.isStopped) {
            return;
        }

        this.bookingsWorker = this.createBookingsWorker();
        this.bookingsWorker.onmessage = ({ data }) =>
            this.handleBookingsResult(data as WorkerToMainMessage);

        this.bookingsWorker.postMessage(this.buildPersistMessage(rawJson));
    }

    private persistCustomers(rawJson: string): void {
        if (this.isStopped) {
            return;
        }

        this.customersWorker = this.createCustomersWorker();
        this.customersWorker.onmessage = ({ data }) =>
            this.handleCustomersResult(data as WorkerToMainMessage);

        this.customersWorker.postMessage(this.buildPersistMessage(rawJson));
    }

    private handleBookingsResult(result: WorkerToMainMessage): void {
        if (result?.kind !== 'PERSIST_RESULT') {
            return;
        }

        if (result.success) {
            this.toastrService.success(
                'Synchronisierung abgeschlossen',
                'Hintergrundprozess abgeschlossen'
            );

            return;
        }

        if (result.error === 'INDEXED_DB_ERROR') {
            this.toastrService.error(
                'An error ocurred while inserting old bookings',
                'Background process error'
            );
        } else {
            this.logger.warn('No records were found', 'Background process warning');
        }
    }

    private handleCustomersResult(result: WorkerToMainMessage): void {
        if (result?.kind !== 'PERSIST_RESULT') {
            return;
        }

        if (result.success) {
            this.toastrService.success(
                'Kundensynchronisierung abgeschlossen',
                'Hintergrundprozess abgeschlossen'
            );

            return;
        }

        if (result.error === 'INDEXED_DB_ERROR') {
            this.toastrService.error(
                'Beim Einfügen der inaktiven Kunden ist ein Fehler aufgetreten',
                'Fehler'
            );
        } else {
            this.logger.warn('No records were found', 'Background process warning');
        }
    }

    private buildPersistMessage(rawJson: string): MainToWorkerMessage {
        return {
            kind: 'PERSIST',
            rawJson,
            dbKey: this.getWorkerDbKey()
        };
    }

    /**
     * The in-memory IndexedDB encryption key to hand to a background worker
     * (Issue #4), or undefined when encryption is disabled / no key is held.
     */
    private getWorkerDbKey(): Uint8Array | undefined {
        return this.dbKeyService.hasKey()
            ? this.dbKeyService.getKey()
            : undefined;
    }

    // The `new Worker(new URL(...), ...)` calls must stay in this exact static
    // form so the Angular CLI worker bundler can statically detect them.
    private createBookingsWorker(): Worker {
        return new Worker(
            new URL('../core/workers/bookings.worker', import.meta.url),
            { type: 'module' }
        );
    }

    private createCustomersWorker(): Worker {
        return new Worker(
            new URL('../core/workers/customers.worker', import.meta.url),
            { type: 'module' }
        );
    }

    private terminateWorker(
        worker: 'bookingsWorker' | 'customersWorker'
    ): void {
        if (this[worker]) {
            this[worker].terminate();
            this[worker] = undefined;
        }
    }
}
